import { useState } from 'react';
import { captureCalculatorEmail } from '../../lib/email-capture';

interface EmailCaptureProps {
  slug: string;
  calculatorInputs: Record<string, number | string>;
  calculatorResults: Record<string, number | string>;
  headline?: string;
  subheadline?: string;
}

export default function EmailCapture({
  slug,
  calculatorInputs,
  calculatorResults,
  headline = 'Get the detailed PDF report',
  subheadline = "We'll email you a breakdown of the math, plus the 3 moves FAME would make to capture this revenue.",
}: EmailCaptureProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;
    setStatus('submitting');
    try {
      await captureCalculatorEmail({
        email,
        name: name || undefined,
        slug,
        calculatorInputs,
        calculatorResults,
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 rounded-2xl p-8 md:p-10 text-white shadow-2xl">
      <h3 className="text-2xl md:text-3xl font-display font-bold mb-2">{headline}</h3>
      <p className="text-navy-100 text-base md:text-lg mb-6">{subheadline}</p>

      {status === 'success' ? (
        <div className="bg-white/15 backdrop-blur-sm rounded-xl p-6 text-center">
          <i className="ri-mail-check-line text-4xl mb-2 block text-orange-400"></i>
          <p className="text-lg font-semibold">You&apos;re on the list.</p>
          <p className="text-navy-100 text-sm">
            Check your inbox in the next few minutes for your detailed report.
          </p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 md:flex-row md:items-start">
          <input
            type="text"
            placeholder="Your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="flex-1 px-4 py-3 rounded-lg text-navy-900 placeholder-navy-400 font-medium focus:outline-none focus:ring-4 focus:ring-teal-500/30"
          />
          <input
            type="email"
            required
            placeholder="you@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="flex-1 px-4 py-3 rounded-lg text-navy-900 placeholder-navy-400 font-medium focus:outline-none focus:ring-4 focus:ring-teal-500/30"
          />
          <button
            type="submit"
            disabled={status === 'submitting'}
            className="btn-primary whitespace-nowrap disabled:opacity-70"
          >
            {status === 'submitting' ? 'Sending…' : 'Send My Report'}
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </form>
      )}

      {status === 'error' && (
        <p className="text-orange-300 text-sm mt-3">
          Something went wrong. Call us at (954) 296-4896 and we&apos;ll send the report manually.
        </p>
      )}
    </div>
  );
}
